import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Clock, Lock, Unlock } from "lucide-react";
import { api, fmtError, BACKEND_URL } from "@/lib/api";
import { fmtEUR, fmtDate, L } from "@/constants/labels";
import { ArtisanActions } from "./ArtisanActions";
import { DecisionModal, ReceptionModal, LeveeDecisionModal, ReponseArtisanModal } from "./DecisionModals";

const T = L.versements;
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

const Ligne = ({ l, role, onAct }) => {
  const [label, cls] = T.statut[l.statut] || [l.statut, "badge-muted"];
  const montant = l.montant_valide_ht ?? l.montant_demande_ht;
  const barre = l.montant_valide_ht != null && l.montant_valide_ht !== l.montant_demande_ht && l.statut === "valide_attente_liberation";
  const pending = ["declare", "en_attente_validation", "levee_declaree"].includes(l.statut);
  const doAction = role === "entreprise" && (pending || (l.type === "retenue" && l.liberable && l.statut === "retenue_constituee"));
  const artisanAction = role === "prestataire" && l.statut === "partiel_propose";
  return (
    <li className="py-2.5 border-b border-[#E4E7EB] last:border-0" data-testid={`chaine-ligne-${l.type}-${l.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-[#1F2933] text-sm">
            {l.label}{l.pct_demande != null && l.type !== "retenue" && <span className="text-[#3E4C59] font-normal"> · {l.pct_valide ?? l.pct_demande} %</span>}
            {l.type === "retenue" && <span className="text-[#3E4C59] font-normal"> · {l.pct_demande} %</span>}
          </div>
          <div className="text-xs text-[#3E4C59] mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className={cls} data-testid={`chaine-statut-${l.id}`}>{label}</span>
            {l.decided_at && l.statut === "valide_attente_liberation" && <span>le {fmtDate(l.decided_at)}</span>}
            {pending && l.deadline_at && <span className="inline-flex items-center gap-1"><Clock size={11} /> avant le {fmtDT(l.deadline_at)}</span>}
            {l.type === "retenue" && l.liberable_le && <span className="inline-flex items-center gap-1">{l.liberable ? <Unlock size={11} /> : <Lock size={11} />} libérable le {fmtDate(l.liberable_le)}{l.reserves_ouvertes ? ` · ${l.reserves_ouvertes} réserve(s) ouverte(s)` : ""}</span>}
          </div>
          {pending && role === "prestataire" && <div className="text-xs text-[#7C3E00] mt-1" data-testid={`chaine-attente-artisan-${l.id}`}>{T.attenteArtisan}</div>}
        </div>
        <div className="text-right shrink-0">
          <div className="font-semibold text-[#1F2933] text-sm tabular-nums" data-testid={`chaine-montant-${l.id}`}>{fmtEUR(montant)}</div>
          {barre && <div className="text-[11px] text-[#3E4C59] line-through tabular-nums">{fmtEUR(l.montant_demande_ht)}</div>}
        </div>
      </div>
      {role === "prestataire" && l.commission && (
        <div className="mt-1 ml-0 sm:ml-3 pl-2 border-l-2 border-[#CFE3D6] text-xs flex flex-wrap justify-between gap-x-3" data-testid={`chaine-commission-${l.id}`}>
          <span className="text-[#3E4C59]">Commission {l.commission.taux_effectif}{"\u00a0"}% : {fmtEUR(l.commission.commission_ht)} HT ({fmtEUR(l.commission.commission_ttc)} TTC){l.commission.previsionnel ? " · prévisionnelle" : l.commission.facture_numero ? ` · facture ${l.commission.facture_numero}` : " · due"}</span>
          <span className="font-semibold text-[#1B7F4B] tabular-nums">Vous percevez {fmtEUR(l.commission.net_percu)}</span>
        </div>
      )}
      {(doAction || artisanAction) && (
        <button className={`mt-2 w-full sm:w-auto ${doAction && l.statut !== "retenue_constituee" ? "btn-accent" : "btn-outline"} text-sm justify-center`} onClick={() => onAct(l)} data-testid={`chaine-action-${l.id}`}>
          {artisanAction ? "Répondre à la validation partielle" : l.type === "retenue" && l.statut === "retenue_constituee" ? "Libérer la retenue (délai expiré)" : l.type === "solde" ? "Prononcer la réception" : l.type === "retenue" ? "Instruire la levée des réserves" : "Confirmer ou contester"}
        </button>
      )}
    </li>
  );
};

/** Bloc unique — toute la chaîne financière d'une commande, lisible sur mobile. */
export const ChaineFinanciere = ({ commandeId, missionId, role, compact = false }) => {
  const [chaine, setChaine] = useState(null);
  const [versements, setVersements] = useState([]);
  const [pv, setPv] = useState(null);
  const [modal, setModal] = useState(null);
  const load = useCallback(async () => {
    try {
      const [c, v] = await Promise.all([api.get(`/commandes/${commandeId}/chaine-financiere`), api.get(`/commandes/${commandeId}/versements`)]);
      setChaine(c.data); setVersements(v.data);
      if (missionId && v.data.some((x) => x.type === "solde" && x.statut === "valide_attente_liberation")) api.get(`/missions/${missionId}/pv`).then((r) => setPv(r.data)).catch(() => {});
    } catch (e) { toast.error(fmtError(e)); }
  }, [commandeId, missionId]);
  useEffect(() => { load(); }, [load]);
  if (!chaine) return null;

  const act = async (l) => {
    const v = versements.find((x) => x.id === l.id);
    if (l.type === "retenue" && l.statut === "retenue_constituee") {
      if (!window.confirm(`Libérer la retenue de garantie (${fmtEUR(l.montant_demande_ht)} HT) ? Délai expiré. Cette décision est horodatée.`)) return;
      try { await api.post(`/versements/${l.id}/liberer-retenue`); toast.success("Retenue validée — en attente de libération"); load(); } catch (e) { toast.error(fmtError(e)); }
      return;
    }
    setModal({ v });
  };
  const done = () => { setModal(null); load(); };
  const m = modal?.v;

  return (
    <div className="bg-white border border-[#E4E7EB] rounded-[6px] p-3 sm:p-4" data-testid={`chaine-financiere-${commandeId}`}>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h3 className="font-semibold text-[#1F2933] text-sm sm:text-base">{T.titre} · Commande {chaine.commande_numero}</h3>
        <div className="text-sm"><b className="tabular-nums" data-testid="chaine-total">{fmtEUR(chaine.commande_ht)}</b> HT</div>
      </div>
      <div className="mt-2 h-2 rounded-full bg-[#EEF1F4] overflow-hidden" aria-label="Avancement validé">
        <div className="h-full bg-[#1B7F4B] transition-[width]" style={{ width: `${Math.min(100, chaine.cumul_valide_pct)}%` }} data-testid="chaine-progress" />
      </div>
      <div className="text-xs text-[#3E4C59] mt-1 flex justify-between"><span>Validé {chaine.cumul_valide_pct} % · {fmtEUR(chaine.cumul_valide_ht)}</span><span>Reste {fmtEUR(chaine.solde_previsionnel_ht)}</span></div>
      <ul className="mt-2">
        {chaine.lignes.length === 0 && <li className="py-3 text-sm text-[#3E4C59]">Aucune étape déclarée.{chaine.acompte_pct > 0 && ` Acompte prévu : ${chaine.acompte_pct} %.`}{chaine.retenue?.active && ` Retenue de garantie : ${chaine.retenue.taux} %.`}</li>}
        {chaine.lignes.map((l) => <Ligne key={l.id} l={l} role={role} onAct={act} />)}
      </ul>
      {role === "prestataire" && !compact && <div className="mt-3"><ArtisanActions chaine={chaine} versements={versements} onChanged={load} /></div>}
      {pv && (
        <a href={`${BACKEND_URL}/api/missions/${missionId}/pv/pdf`} target="_blank" rel="noreferrer" className="btn-outline text-xs mt-3 inline-flex" data-testid="pv-pdf-link"><FileText size={12} /> {T.pvPdf}{pv.type_reception === "avec_reserves" ? ` — ${pv.reserves?.length} réserve(s)` : ""}</a>
      )}
      {role === "prestataire" && chaine.commission && (
        <div className="mt-3 text-xs flex flex-wrap justify-between gap-x-3 bg-[#F8FAF9] rounded p-2" data-testid="chaine-commission-total">
          <span>Commission plateforme sur la commande : {fmtEUR(chaine.commission.commission_ht)} HT ({fmtEUR(chaine.commission.commission_ttc)} TTC) · due à ce jour {fmtEUR(chaine.commission.due_ht)} HT, en attente de prélèvement</span>
          <span className="font-semibold text-[#1B7F4B]">Net total : {fmtEUR(chaine.commission.net_percu)}</span>
        </div>
      )}
      {role === "entreprise" && chaine.commission_info && <p className="text-[11px] text-[#94A3B8] mt-2" data-testid="chaine-commission-info">{chaine.commission_info}</p>}
      <p className="text-[11px] text-[#3E4C59] mt-3">{role === "entreprise" ? T.aucuneLiberation : T.sequestreInfo}</p>

      {m && (m.type === "acompte" || m.type === "situation") && role === "entreprise" && <DecisionModal v={m} chaine={chaine} onClose={() => setModal(null)} onDone={done} />}
      {m && m.type === "solde" && role === "entreprise" && <ReceptionModal v={m} chaine={chaine} onClose={() => setModal(null)} onDone={done} />}
      {m && m.type === "retenue" && role === "entreprise" && <LeveeDecisionModal v={m} onClose={() => setModal(null)} onDone={done} />}
      {m && role === "prestataire" && <ReponseArtisanModal v={m} onClose={() => setModal(null)} onDone={done} />}
    </div>
  );
};

/** Wrapper mission : trouve la commande active puis affiche la chaîne. */
export const MissionVersements = ({ mission, role }) => {
  const [cid, setCid] = useState(undefined);
  useEffect(() => {
    api.get(`/commandes?mission_id=${mission.id}`).then((r) => {
      const c = (r.data || []).find((x) => ["acceptee", "facturee_partielle", "soldee"].includes(x.statut));
      setCid(c ? c.id : null);
    }).catch(() => setCid(null));
  }, [mission.id]);
  if (cid === undefined) return null;
  if (!cid) return <div className="text-xs text-[#3E4C59]" data-testid={`mission-versements-none-${mission.id}`}>Les versements (acompte, situations, solde) apparaîtront dès qu'une commande de travaux sera acceptée.</div>;
  return <ChaineFinanciere commandeId={cid} missionId={mission.id} role={role} />;
};
